import type { UploadedImage, StatusTag } from '@/lib/validations/product-submission';
import type { PreviewImageDto } from '@/lib/services/productService';
import type { UserSearchResult } from '@/lib/types/team';

// ============================================================================
// BLOCK TYPES
// ============================================================================

export type BlockType = 'text' | 'image' | 'video' | 'gallery';

export type TextVariant = 'paragraph' | 'heading1' | 'heading2' | 'heading3' | 'quote';
export type ImageSize = 'small' | 'medium' | 'large' | 'full';
export type ImageAlignment = 'left' | 'center' | 'right';
export type VideoProvider = 'youtube' | 'vimeo';
export type GalleryColumns = 2 | 3 | 4;

// ============================================================================
// BLOCK DEFINITIONS
// ============================================================================

interface BaseBlock {
  id: string;
  type: BlockType;
  createdAt: number;
  updatedAt: number;
}

export interface TextBlock extends BaseBlock {
  type: 'text';
  content: {
    html: string;
    plainText: string;
  };
  style: {
    variant: TextVariant;
  };
}

export interface ImageBlock extends BaseBlock {
  type: 'image';
  content: {
    image: UploadedImage | null;
    caption?: string;
    alt?: string;
  };
  style: {
    size: ImageSize;
    alignment: ImageAlignment;
  };
}

export interface VideoBlock extends BaseBlock {
  type: 'video';
  content: {
    url: string;
    embedId: string;
    provider: VideoProvider;
    caption?: string;
    // Custom embed (any video platform): the player src URL, parsed once
    // from the pasted iframe/link. Player options live in its query params.
    embedSrc?: string;
    inputMode?: 'url' | 'iframe'; // Track which input method user chose
    // Intrinsic aspect ratio (width / height) parsed from the embed code so
    // the player fills its box instead of being pillar/letter-boxed inside a
    // hardcoded 16:9 frame. Undefined → fall back to 16:9.
    aspectRatio?: number;
    // Zoom of the video INSIDE its frame, % of the frame (100–200).
    // The frame itself never changes size; the enlarged video is cropped
    // by it. Undefined → 100.
    scalePercent?: number;
    // Pan of the zoomed video within the frame, % of the frame. Clamped to
    // ±(scalePercent − 100) / 2 so edges never show. Undefined → 0.
    offsetX?: number;
    offsetY?: number;
  };
}

// Step/clamp policy for the video zoom & pan controls.
// 1% per click — pinpoint framing beats fewer clicks here.
export const EMBED_SCALE_STEP = 1;
export const EMBED_SCALE_MIN = 100;
export const EMBED_SCALE_MAX = 200;
export const EMBED_OFFSET_STEP = 1;

/** Largest pan (either sign) that keeps the zoomed video covering the frame. */
export function embedMaxOffset(scalePercent: number): number {
  return Math.max(0, (scalePercent - EMBED_SCALE_MIN) / 2);
}

export interface GalleryBlock extends BaseBlock {
  type: 'gallery';
  content: {
    images: UploadedImage[];
    captions?: string[];
  };
  style: {
    columns: GalleryColumns;
  };
}

export type ContentBlock = TextBlock | ImageBlock | VideoBlock | GalleryBlock;

// ============================================================================
// FORM DATA STRUCTURE
// ============================================================================

export interface PageBuilderHeader {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  logo: UploadedImage | null;
  websiteUrl: string;
}

export interface PendingInvitation {
  user: UserSearchResult;
  role: string;
}

export interface PageBuilderMetadata {
  categoryIds: number[];
  statusTags: StatusTag[];
  previewImages?: PreviewImageDto[];
  pendingInvitations?: PendingInvitation[];
  launchDate?: string;
}

export interface PageBuilderFormData {
  header: PageBuilderHeader;
  blocks: ContentBlock[];
  metadata: PageBuilderMetadata;
}

// ============================================================================
// DRAFT DATA
// ============================================================================

export interface PageBuilderDraft extends PageBuilderFormData {
  savedAt: number;
  version: number;
}

// ============================================================================
// BLOCK OPERATIONS
// ============================================================================

export type BlockOperation =
  | { type: 'add'; block: ContentBlock; afterId?: string }
  | { type: 'update'; id: string; updates: Partial<ContentBlock> }
  | { type: 'delete'; id: string }
  | { type: 'move'; fromIndex: number; toIndex: number }
  | { type: 'duplicate'; id: string };

// ============================================================================
// BLOCK INSERT MENU
// ============================================================================

export interface BlockTypeOption {
  type: BlockType;
  label: string;
  description: string;
  icon: string;
  keywords: string[];
}

export const BLOCK_TYPE_OPTIONS: BlockTypeOption[] = [
  {
    type: 'text',
    label: 'Текст',
    description: 'Заголовок, параграф или цитата',
    icon: 'Type',
    keywords: ['текст', 'параграф', 'заголовок', 'text', 'paragraph', 'heading'],
  },
  {
    type: 'image',
    label: 'Изображение',
    description: 'Фото или скриншот с подписью',
    icon: 'Image',
    keywords: ['изображение', 'фото', 'картинка', 'image', 'photo', 'picture'],
  },
  {
    type: 'video',
    label: 'Видео',
    description: 'YouTube, Vimeo, или iframe',
    icon: 'Video',
    keywords: ['видео', 'youtube', 'vimeo', 'iframe', 'video'],
  },
  {
    type: 'gallery',
    label: 'Галерея',
    description: 'Несколько изображений в сетке',
    icon: 'LayoutGrid',
    keywords: ['галерея', 'сетка', 'gallery', 'grid', 'images'],
  },
];

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

export function createBlock(type: BlockType): ContentBlock {
  const now = Date.now();
  const id = `block_${now}_${Math.random().toString(36).slice(2, 9)}`;

  const base = { id, createdAt: now, updatedAt: now };

  switch (type) {
    case 'text':
      return {
        ...base,
        type: 'text',
        content: { html: '', plainText: '' },
        style: { variant: 'paragraph' },
      };
    case 'image':
      return {
        ...base,
        type: 'image',
        content: { image: null, caption: '', alt: '' },
        style: { size: 'large', alignment: 'center' },
      };
    case 'video':
      return {
        ...base,
        type: 'video',
        content: { url: '', embedId: '', provider: 'youtube', caption: '', inputMode: 'iframe' },
      };
    case 'gallery':
      return {
        ...base,
        type: 'gallery',
        content: { images: [], captions: [] },
        style: { columns: 2 },
      };
  }
}

export function createEmptyFormData(): PageBuilderFormData {
  return {
    header: {
      slug: '',
      name: '',
      tagline: '',
      description: '',
      logo: null,
      websiteUrl: '',
    },
    blocks: [],
    metadata: {
      categoryIds: [],
      statusTags: [],
      previewImages: [],
      pendingInvitations: [],
    },
  };
}

// ============================================================================
// VIDEO HELPERS
// ============================================================================

export function parseVideoUrl(url: string): { provider: VideoProvider; embedId: string } | null {
  // YouTube patterns
  const youtubePatterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of youtubePatterns) {
    const match = url.match(pattern);
    if (match) {
      return { provider: 'youtube', embedId: match[1] };
    }
  }

  // Vimeo patterns
  const vimeoPatterns = [
    /vimeo\.com\/(\d+)/,
    /player\.vimeo\.com\/video\/(\d+)/,
  ];

  for (const pattern of vimeoPatterns) {
    const match = url.match(pattern);
    if (match) {
      return { provider: 'vimeo', embedId: match[1] };
    }
  }

  return null;
}

export function getVideoEmbedUrl(provider: VideoProvider, embedId: string): string {
  switch (provider) {
    case 'youtube':
      return `https://www.youtube.com/embed/${embedId}`;
    case 'vimeo':
      return `https://player.vimeo.com/video/${embedId}`;
  }
}

export interface ParsedEmbedInput {
  /** Player src URL with provider defaults baked into its query params. */
  src: string;
  /** Ratio recovered from the pasted markup, or null when it carries none. */
  aspectRatio: number | null;
}

/**
 * Parses user embed input ONCE at the input boundary. Accepts a pasted
 * <iframe> snippet (with or without a responsive wrapper) or a bare player
 * URL. Everything downstream stores and renders the returned {src, ratio} —
 * the pasted HTML is never persisted or re-parsed.
 */
export function parseEmbedInput(input: string): ParsedEmbedInput | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const iframeMatch = trimmed.match(/<iframe[^>]*>[\s\S]*?<\/iframe>/i);
  if (iframeMatch) {
    const src = extractIframeSrc(iframeMatch[0]);
    if (!src) return null;
    // Ratio must be read from the RAW input — the responsive wrapper
    // (where Kinescope/Vimeo encode it via padding) sits outside the tag.
    return { src: normalizeEmbedSrc(src), aspectRatio: extractIframeAspectRatio(trimmed) };
  }

  // Bare player URL — the editor shows the stored src when re-editing,
  // so a blur without changes must round-trip losslessly.
  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return { src: normalizeEmbedSrc(trimmed), aspectRatio: null };
  } catch {
    return null;
  }
}

/**
 * Extracts the source URL from an iframe HTML string.
 * Decodes HTML entities (e.g. &amp; → &) so the returned value is a valid URL.
 */
export function extractIframeSrc(iframeHtml: string): string | null {
  const srcMatch = iframeHtml.match(/src=["']([^"']+)["']/i);
  if (!srcMatch) return null;
  return srcMatch[1]
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

/** Default 16:9 ratio used when an embed's true ratio can't be determined. */
export const DEFAULT_VIDEO_ASPECT_RATIO = 16 / 9;

function readNumericIframeAttr(tag: string, attr: 'width' | 'height'): number | null {
  const match = tag.match(
    new RegExp(`\\b${attr}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i')
  );
  if (!match) return null;
  const raw = (match[1] ?? match[2] ?? match[3] ?? '').trim();
  // Percentage / non-numeric values (e.g. width="100%") carry no ratio info.
  if (!raw || raw.includes('%')) return null;
  const num = parseFloat(raw.replace(/px$/i, ''));
  return Number.isFinite(num) && num > 0 ? num : null;
}

function clampAspectRatio(ratio: number): number | null {
  if (!Number.isFinite(ratio) || ratio <= 0) return null;
  // Guard against pathological values while still allowing everything from
  // portrait (≈0.56) through ultrawide (≈2.4) embeds.
  return Math.min(Math.max(ratio, 0.4), 4);
}

/**
 * Determines the intrinsic aspect ratio (width / height) of an embed from its
 * raw HTML. Embeds encode this in one of two ways:
 *   1. A responsive wrapper using a `padding-top`/`padding-bottom` percentage
 *      (Kinescope, Vimeo, ...). The padding percentage is relative to the
 *      container WIDTH, so it expresses height as a fraction of width and
 *      ratio = 100 / percentage.
 *   2. Numeric `width`/`height` attributes on the <iframe> itself.
 *
 * Pass the RAW pasted input (including any wrapper <div>) — `parseEmbedInput`
 * strips the wrapper, which is where the ratio usually lives. Returns the
 * ratio clamped to a sane range, or null when it can't be determined (callers
 * fall back to 16:9).
 */
export function extractIframeAspectRatio(input: string): number | null {
  if (!input) return null;

  const paddingMatch = input.match(/padding-(?:top|bottom)\s*:\s*([\d.]+)%/i);
  if (paddingMatch) {
    const percent = parseFloat(paddingMatch[1]);
    if (percent > 0) return clampAspectRatio(100 / percent);
  }

  const iframeTag = input.match(/<iframe[^>]*>/i)?.[0] ?? input;
  const width = readNumericIframeAttr(iframeTag, 'width');
  const height = readNumericIframeAttr(iframeTag, 'height');
  if (width && height) return clampAspectRatio(width / height);

  return null;
}

export type EmbedProvider = 'kinescope' | 'vimeo' | 'youtube' | 'other';

export interface IframeEmbedOptions {
  autoplay: boolean;
  muted: boolean;
  loop: boolean;
  controls: boolean;
}

const DEFAULT_EMBED_OPTIONS: IframeEmbedOptions = {
  autoplay: true,
  muted: true,
  loop: true,
  controls: false,
};

// Kinescope embed URLs live on the apex domain and use one of these
// path shapes: `/embed/{videoId}` or `/{videoId}`. The video id is an
// alphanumeric string (dashes/underscores allowed), typically 10-22 chars.
const KINESCOPE_HOSTS = new Set([
  'kinescope.io',
  'www.kinescope.io',
  'kinescope.ru',
  'www.kinescope.ru',
  'kinescope.com',
  'www.kinescope.com',
]);
const KINESCOPE_PATH_RE = /^\/(?:embed\/)?[A-Za-z0-9_-]{8,64}\/?$/;

function isKinescopeUrl(url: URL): boolean {
  if (!KINESCOPE_HOSTS.has(url.hostname.toLowerCase())) return false;
  return KINESCOPE_PATH_RE.test(url.pathname);
}

function isVimeoUrl(url: URL): boolean {
  const hostname = url.hostname.toLowerCase();
  return (
    hostname === 'vimeo.com' ||
    hostname.endsWith('.vimeo.com') ||
    hostname === 'player.vimeo.com'
  );
}

function isYoutubeUrl(url: URL): boolean {
  const hostname = url.hostname.toLowerCase();
  return (
    hostname === 'youtube.com' ||
    hostname.endsWith('.youtube.com') ||
    hostname === 'youtu.be' ||
    hostname === 'youtube-nocookie.com' ||
    hostname.endsWith('.youtube-nocookie.com')
  );
}

export function detectEmbedProvider(src: string | null | undefined): EmbedProvider {
  if (!src) return 'other';
  try {
    const url = new URL(src);
    if (isKinescopeUrl(url)) return 'kinescope';
    if (isVimeoUrl(url)) return 'vimeo';
    if (isYoutubeUrl(url)) return 'youtube';
    return 'other';
  } catch {
    return 'other';
  }
}

/**
 * Provider is considered configurable when we know how to read/write
 * its player options via URL parameters (currently Kinescope & Vimeo).
 */
export function isConfigurableEmbedProvider(
  provider: EmbedProvider
): provider is 'kinescope' | 'vimeo' {
  return provider === 'kinescope' || provider === 'vimeo';
}

/**
 * Normalizes a player src URL at parse time. For providers with known
 * player APIs (Kinescope, Vimeo) the default options are baked into the
 * query string — render sites never touch the URL again.
 */
export function normalizeEmbedSrc(src: string): string {
  try {
    const url = new URL(src);

    if (isKinescopeUrl(url) || isVimeoUrl(url)) {
      // Only apply defaults when the user hasn't already set them,
      // so toggling options in the editor is preserved on re-normalization.
      if (!url.searchParams.has('autoplay')) {
        url.searchParams.set('autoplay', DEFAULT_EMBED_OPTIONS.autoplay ? '1' : '0');
      }
      if (!url.searchParams.has('muted')) {
        url.searchParams.set('muted', DEFAULT_EMBED_OPTIONS.muted ? '1' : '0');
      }
      if (!url.searchParams.has('loop')) {
        url.searchParams.set('loop', DEFAULT_EMBED_OPTIONS.loop ? '1' : '0');
      }
      if (!url.searchParams.has('controls')) {
        url.searchParams.set('controls', DEFAULT_EMBED_OPTIONS.controls ? '1' : '0');
      }
      // Both players pause an embed when another instance starts playing —
      // the builder mounts two (editor canvas + preview panel), which
      // otherwise kills the muted autoplay a moment after it starts.
      if (!url.searchParams.has('autopause')) {
        url.searchParams.set('autopause', '0');
      }
    }

    return url.toString();
  } catch {
    return src;
  }
}

/**
 * Reads the current player option values from a player src URL.
 * Returns defaults for any params that are absent.
 */
export function getEmbedSrcOptions(src: string): IframeEmbedOptions {
  try {
    const url = new URL(src);
    const read = (key: keyof IframeEmbedOptions, fallback: boolean): boolean => {
      const value = url.searchParams.get(key);
      if (value === null) return fallback;
      return value === '1' || value === 'true';
    };
    return {
      autoplay: read('autoplay', DEFAULT_EMBED_OPTIONS.autoplay),
      muted: read('muted', DEFAULT_EMBED_OPTIONS.muted),
      loop: read('loop', DEFAULT_EMBED_OPTIONS.loop),
      controls: read('controls', DEFAULT_EMBED_OPTIONS.controls),
    };
  } catch {
    return { ...DEFAULT_EMBED_OPTIONS };
  }
}

/**
 * Writes the player option values into a player src URL and returns the
 * updated URL. Non-URL input is returned as-is.
 */
export function setEmbedSrcOptions(src: string, options: IframeEmbedOptions): string {
  try {
    const url = new URL(src);
    url.searchParams.set('autoplay', options.autoplay ? '1' : '0');
    url.searchParams.set('muted', options.muted ? '1' : '0');
    url.searchParams.set('loop', options.loop ? '1' : '0');
    url.searchParams.set('controls', options.controls ? '1' : '0');
    return url.toString();
  } catch {
    return src;
  }
}
